import { useCallback, type KeyboardEvent, type MouseEvent } from 'react';
import {
  BellIcon,
  ClipboardDocumentListIcon,
  UserPlusIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  BriefcaseIcon,
  ArchiveBoxIcon,
  ArrowsRightLeftIcon,
  DocumentArrowUpIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { Notification } from '../types/notification.types';

type NotificationItemTheme = 'default' | 'edgemail';

interface NotificationItemProps {
  notification: Notification;
  onMarkRead: (id: string) => void;
  onNavigate?: (link: string) => void;
  onDelete?: (id: string) => void;
  theme?: NotificationItemTheme;
}

const ICON_MAP: Record<string, typeof BellIcon> = {
  task_reminder: BellIcon,
  task_assigned: UserPlusIcon,
  task_overdue: ExclamationTriangleIcon,
  task_completed: CheckCircleIcon,
  general: ClipboardDocumentListIcon,
  // Matter lifecycle notifications
  matter_created: BriefcaseIcon,
  matter_status_changed: BriefcaseIcon,
  matter_deleted: TrashIcon,
  matter_archived: ArchiveBoxIcon,
  matter_transferred: ArrowsRightLeftIcon,
  matter_document_uploaded: DocumentArrowUpIcon,
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function NotificationItem({
  notification,
  onMarkRead,
  onNavigate,
  onDelete,
  theme = 'default',
}: NotificationItemProps) {
  const Icon = ICON_MAP[notification.type] || BellIcon;
  const isEdgeMail = theme === 'edgemail';

  const handleClick = useCallback(() => {
    if (!notification.is_read) onMarkRead(notification.id);
    if (notification.link && onNavigate) onNavigate(notification.link);
  }, [notification, onMarkRead, onNavigate]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  const handleDelete = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onDelete?.(notification.id);
  }, [notification.id, onDelete]);

  const metadata = (notification.metadata || {}) as Record<string, unknown>;
  const sourceApp = typeof metadata.source_app === 'string' ? metadata.source_app : undefined;
  const originApp = typeof metadata.origin_app === 'string' ? metadata.origin_app : undefined;

  const formatSource = (value?: string) => {
    if (!value) return null;
    switch (value) {
      case 'edgemail': return 'Email';
      case 'tasks': return 'Tasks';
      case 'notes': return 'Notes';
      case 'documents': return 'Documents';
      case 'calendar': return 'Calendar';
      case 'matters': return 'Matters';
      default: return value;
    }
  };

  const sourceLabel = formatSource(sourceApp);
  const originLabel = originApp && originApp !== sourceApp ? formatSource(originApp) : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors cursor-pointer ${
        isEdgeMail
          ? `hover:bg-[var(--em-state-hover)] ${!notification.is_read ? 'bg-[var(--em-state-selected)]' : ''}`
          : `hover:bg-[var(--brand-surface-hover)] ${!notification.is_read ? 'bg-[var(--brand-accent-primary-soft)]' : ''}`
      }`}
    >
      <div
        className={`mt-0.5 flex-shrink-0 p-1.5 rounded-full ${
          isEdgeMail
            ? !notification.is_read
              ? 'bg-[var(--em-accent-primary-light)] text-[var(--em-accent-primary)]'
              : 'bg-[var(--em-surface-toolbar)] text-[var(--em-text-tertiary)]'
            : !notification.is_read
              ? 'bg-[var(--brand-accent-primary-soft)] text-[var(--brand-accent-primary)]'
              : 'bg-[var(--brand-surface-muted)] text-[var(--brand-text-tertiary)]'
        }`}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm leading-tight ${
            isEdgeMail
              ? !notification.is_read
                ? 'font-medium text-[var(--em-text-primary)]'
                : 'text-[var(--em-text-secondary)]'
              : !notification.is_read
                ? 'font-medium text-[var(--brand-text-primary)]'
                : 'text-[var(--brand-text-secondary)]'
          }`}
        >
          {notification.title}
        </p>
        {notification.message && (
          <p className={`text-xs mt-0.5 truncate ${isEdgeMail ? 'text-[var(--em-text-tertiary)]' : 'text-[var(--brand-text-tertiary)]'}`}>
            {notification.message}
          </p>
        )}
        {(sourceLabel || originLabel) && (
          <p className={`text-[11px] mt-1 ${isEdgeMail ? 'text-[var(--em-text-muted)]' : 'text-[var(--brand-text-muted)]'}`}>
            {sourceLabel && <span>Source: {sourceLabel}</span>}
            {sourceLabel && originLabel && <span className="mx-1">•</span>}
            {originLabel && <span>Origin: {originLabel}</span>}
          </p>
        )}
        <p className={`text-xs mt-1 ${isEdgeMail ? 'text-[var(--em-text-muted)]' : 'text-[var(--brand-text-muted)]'}`}>
          {timeAgo(notification.created_at)}
        </p>
      </div>
      <button
        onClick={handleDelete}
        className={`mt-0.5 p-1 rounded-md transition-colors ${
          isEdgeMail
            ? 'text-[var(--em-text-tertiary)] hover:text-[var(--em-status-danger-text)] hover:bg-[var(--em-state-hover)]'
            : 'text-[var(--brand-text-tertiary)] hover:text-red-600 hover:bg-[var(--brand-surface-hover)]'
        }`}
        title="Dismiss notification"
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
      {!notification.is_read && (
        <span
          className={`mt-1.5 flex-shrink-0 w-2 h-2 rounded-full ${
            isEdgeMail ? 'bg-[var(--em-accent-primary)]' : 'bg-[var(--brand-accent-primary)]'
          }`}
        />
      )}
    </div>
  );
}
